import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wallet,
  Boxes,
  FileText,
  Users,
  ShoppingCart,
  BarChart3,
  Fingerprint,
  Calculator,
  Sparkles,
  ShieldCheck,
  Zap,
  ArrowRight,
  Bot,
} from "lucide-react";
import { Reveal } from "./motion";

const categories = [
  {
    key: "caixa",
    icon: Wallet,
    tag: "Caixa & Financeiro",
    title: "Abre, fecha e movimenta o caixa por você",
    prompt: "Talos, qual o saldo do caixa agora?",
    reply:
      "R$ 3.284,50 em espécie na Filial Centro. Entrada de R$ 5.120 e saída de R$ 1.835,50 hoje. Deseja que eu registre uma sangria?",
    actions: [
      "Consultar saldo em tempo real",
      "Abrir e fechar caixa por filial",
      "Registrar sangria e suprimento",
      "Relatório de fechamento por forma de pagamento",
    ],
  },
  {
    key: "estoque",
    icon: Boxes,
    tag: "Estoque & Compras",
    title: "Prevê o que vai faltar antes de acabar",
    prompt: "O que vai faltar essa semana?",
    reply:
      "Baseado no ritmo dos últimos 14 dias, você vai precisar repor Coca-Cola 2L (3 dias), Leite Piracanjuba (4 dias) e Pão de Forma (5 dias).",
    actions: [
      "Buscar produto por nome, marca ou código de barras",
      "Listar itens com estoque baixo",
      "Histórico de entradas e saídas",
      "Previsão de reposição com base em venda real",
    ],
  },
  {
    key: "fiscal",
    icon: FileText,
    tag: "Fiscal & NF-e",
    title: "Decifra a SEFAZ e sugere o enquadramento correto",
    prompt: "Por que a nota 1287 foi rejeitada?",
    reply:
      "Rejeição 539: NCM 22021000 requer CFOP 5405 (Simples Nacional, venda interna). Quer que eu ajuste e reenvie agora?",
    actions: [
      "Consultar regime fiscal e certificado A1",
      "Status da SEFAZ em tempo real",
      "Explicar rejeição em português simples",
      "Sugerir NCM, CEST, CFOP, ICMS, PIS, COFINS e IBS/CBS",
    ],
  },
  {
    key: "crm",
    icon: Users,
    tag: "CRM & Clientes",
    title: "Localiza clientes, histórico e oportunidades",
    prompt: "Quem são os clientes que não compram há 30 dias?",
    reply:
      "Encontrei 18 clientes ativos que compraram no mês passado mas não voltaram. Deseja que eu gere uma campanha de WhatsApp para eles?",
    actions: [
      "Buscar cliente por CPF, CNPJ ou telefone",
      "Histórico de compras e ticket médio",
      "Clientes inativos e aniversariantes",
      "Sugestões de abordagem comercial",
    ],
  },
  {
    key: "pdv",
    icon: ShoppingCart,
    tag: "PDV & Vendas",
    title: "Informa metas, ranking de vendedores e tíquetes",
    prompt: "Quem é o vendedor que mais vendeu hoje?",
    reply:
      "Lucas lidera o ranking hoje com R$ 2.450 em 14 vendas. A meta do turno está em 82% batida.",
    actions: [
      "Ranking de vendedores em tempo real",
      "Consultar venda por ID ou número de cupom",
      "Total vendido por canal e filial",
      "Produtos mais vendidos do dia",
    ],
  },
  {
    key: "dre",
    icon: BarChart3,
    tag: "DRE & Indicadores",
    title: "Explica a saúde financeira em português claro",
    prompt: "Como fechou a margem deste mês?",
    reply:
      "Faturamento de R$ 84.300 com margem líquida de 18,4%. Seu maior custo foi reposição de estoque (52%) e folha (21%).",
    actions: [
      "Resumo de DRE em tempo real",
      "Margem bruta e líquida",
      "Maiores centros de custo",
      "Comparativo com o mês anterior",
    ],
  },
  {
    key: "contabilidade",
    icon: Calculator,
    tag: "Contabilidade",
    title: "Faz a contabilidade preditiva da sua empresa",
    prompt: "Talos, consegue gerar os holerites e fechar a folha?",
    reply:
      "Sim! Os 142 holerites de abril estão prontos e conferidos, com 13º e comissões calculados. Quer que eu transmita ao eSocial agora?",
    actions: [
      "Holerites e rescisões em 1 clique",
      "Fechamento de folha e cálculo de 13º",
      "Comissões integradas à folha",
      "Transmissão para o eSocial com validação prévia",
    ],
  },
  {
    key: "ponto",
    icon: Fingerprint,
    tag: "Ponto Digital",
    title: "Controla jornadas, horas extras e presenças",
    prompt: "Alguém fez hora extra hoje na Filial 2?",
    reply:
      "Sim, Marcos bateu a saída 42 minutos após o horário previsto (18:42). Registro fotográfico e geolocalização conferidos.",
    actions: [
      "Auditoria de ponto com foto e GPS (MTP)",
      "Espelho de ponto consolidado",
      "Alerta de faltas e atrasos",
      "Cálculo automático de banco de horas",
    ],
  },
  {
    key: "proativo",
    icon: Zap,
    tag: "Proatividade & Automações",
    title: "Monitora tudo em segundo plano e te alerta",
    prompt: "Talos, tem algum alerta importante agora?",
    reply:
      "Atenção: 2 produtos atingiram estoque mínimo e 1 colaborador da filial Norte não registrou ponto de entrada até as 08:30.",
    actions: [
      "Disparo de notificações inteligentes",
      "Alertas preventivos de ruptura de estoque",
      "Detecção de anomalias no fechamento de caixa",
      "Automações operacionais programadas",
    ],
  },
];

export const TalosCapabilities = () => {
  const [activeKey, setActiveKey] = useState("caixa");
  const active = categories.find((c) => c.key === activeKey) || categories[0];
  const ActiveIcon = active.icon;

  return (
    <section
      id="talos"
      className="relative bg-white py-24 transition-colors duration-500 dark:bg-navy-950 md:py-32"
      data-testid="talos-capabilities-section"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        {/* Section Header */}
        <Reveal className="mx-auto mb-16 max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-navy-100 bg-lavender/40 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-navy-800 dark:border-white/10 dark:bg-navy-800 dark:text-lavender">
            <Sparkles className="h-3.5 w-3.5" /> IA Especialista em Operações
          </div>
          <h2 className="text-4xl font-black tracking-tight text-navy-800 dark:text-white sm:text-5xl">
            Conheça o Talos. A IA que opera o sistema com você.
          </h2>
          <p className="mt-5 text-lg font-semibold text-navy-400 dark:text-navy-100/70">
            O Talos não é um chatbot genérico. Ele tem acesso seguro aos dados da sua empresa e executa comandos reais de estoque, vendas, ponto e financeiro.
          </p>
        </Reveal>

        {/* 8 Categories Navigation Pills */}
        <div className="flex flex-wrap items-center justify-center gap-2.5 md:gap-3">
          {categories.map((c) => {
            const Icon = c.icon;
            const isSelected = c.key === activeKey;
            return (
              <button
                key={c.key}
                onClick={() => setActiveKey(c.key)}
                data-testid={`talos-tab-${c.key}`}
                className={`flex items-center gap-2 rounded-full px-4 py-2.5 text-xs font-bold transition-all duration-300 ${
                  isSelected
                    ? "bg-navy-800 text-white shadow-lg shadow-navy-800/30 scale-105 dark:bg-white dark:text-navy-900"
                    : "border border-navy-100 bg-ice text-navy-600 hover:border-navy-200 hover:bg-white dark:border-white/10 dark:bg-navy-900/60 dark:text-navy-100/70 dark:hover:text-white"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                <span>{c.tag}</span>
              </button>
            );
          })}
        </div>

        {/* Interactive Capability Card */}
        <div className="mt-12">
          <AnimatePresence mode="wait">
            <motion.div
              key={active.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="rounded-3xl border border-navy-100 bg-gradient-to-br from-white to-ice p-8 shadow-xl dark:border-white/10 dark:from-navy-900 dark:to-navy-950 md:p-12"
            >
              <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center">
                {/* Left Side: Description & Actions */}
                <div className="lg:col-span-6 space-y-6">
                  <div className="inline-flex items-center gap-2 rounded-full bg-lavender/50 dark:bg-white/10 px-3.5 py-1.5 text-xs font-bold text-navy-800 dark:text-lavender">
                    <ActiveIcon className="h-4 w-4" />
                    <span>{active.tag}</span>
                  </div>

                  <h3 className="text-3xl font-black tracking-tight text-navy-800 dark:text-white sm:text-4xl">
                    {active.title}
                  </h3>

                  <p className="text-sm font-medium text-navy-400 dark:text-navy-100/70">
                    O Talos entende o contexto do seu negócio e executa ações de forma precisa sem que você precise navegar por dezenas de menus.
                  </p>

                  <div className="space-y-3 pt-2">
                    <p className="text-xs font-bold uppercase tracking-wider text-navy-300 dark:text-navy-100/50">
                      O que o Talos pode fazer:
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      {active.actions.map((act, i) => (
                        <div
                          key={i}
                          className="flex items-start gap-2 rounded-xl bg-white dark:bg-navy-800/80 p-3 text-xs font-semibold text-navy-700 dark:text-navy-100 border border-navy-50 dark:border-white/5"
                        >
                          <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-navy-800 text-white dark:bg-lavender dark:text-navy-900 mt-0.5">
                            <ArrowRight className="h-2.5 w-2.5" />
                          </span>
                          <span>{act}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right Side: Visual Chat Simulation */}
                <div className="lg:col-span-6">
                  <div className="rounded-2xl border border-navy-100 bg-white p-6 shadow-xl dark:border-white/10 dark:bg-navy-900">
                    <div className="flex items-center justify-between border-b border-navy-50 dark:border-white/10 pb-4 mb-5">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-navy-800 text-lavender shadow-md">
                          <Bot className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="text-sm font-black text-navy-800 dark:text-white flex items-center gap-1.5">
                            Talos <span className="text-[10px] rounded-full bg-lavender/60 dark:bg-white/10 px-2 py-0.5 text-navy-800 dark:text-lavender font-bold">Assistente</span>
                          </p>
                          <p className="text-[10px] font-semibold text-emerald-500">● Conectado ao MUFT ERP</p>
                        </div>
                      </div>
                      <span className="text-xs font-bold text-navy-300 dark:text-navy-100/40">Exemplo real</span>
                    </div>

                    <div className="space-y-4">
                      {/* User Bubble */}
                      <div className="flex justify-end">
                        <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-navy-800 p-4 text-xs font-medium text-white shadow-sm">
                          {active.prompt}
                        </div>
                      </div>

                      {/* Talos Bubble */}
                      <div className="flex items-start gap-3">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-navy-800 text-lavender mt-1 shadow-sm">
                          <Sparkles className="h-3.5 w-3.5" />
                        </div>
                        <div className="max-w-[85%] rounded-2xl rounded-bl-sm border border-navy-100 bg-ice dark:border-white/10 dark:bg-navy-800 p-4 text-xs font-medium leading-relaxed text-navy-800 dark:text-white/90">
                          {active.reply}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default TalosCapabilities;
